import React, { useEffect, useMemo, useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { PreviewResult, MAX_PREVIEW_IMAGES } from "./LabelAutocomplete";

const MAX_PREVIEW_ROWS = 2;
const PREVIEW_COLUMNS = 10;
const PREVIEW_THUMB_SIZE = 56;
const ROW_SCROLL_STEP = (PREVIEW_THUMB_SIZE + 4) * 3;

type PreviewImage = PreviewResult["images"][number];
type PreviewLayout = "grid" | "row";

export interface PreviewDialogClasses {
  filePreviewDialogTitle?: string;
  filePreviewDialogTitleText?: string;
  filePreviewDialogCloseButton?: string;
  filePreviewImage?: string;
}

interface Props {
  images: PreviewImage[];
  total: number;
  layout?: PreviewLayout;
  dialogClasses?: PreviewDialogClasses;
}

const isImageUrlReachable = (url: string): Promise<boolean> =>
  new Promise((resolve) => {
    if (!url) {
      resolve(false);
      return;
    }

    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });

const filterReachableImages = async (
  images: PreviewImage[],
): Promise<PreviewImage[]> => {
  const checked = await Promise.all(
    images.map(async (image) =>
      (await isImageUrlReachable(image.url)) ? image : null,
    ),
  );

  return checked.filter((image): image is PreviewImage => image !== null);
};

const buildSourceImagesKey = (images: PreviewImage[]) =>
  images.map((image) => `${image.id}:${image.url}`).join("|");

const defaultPreviewImageStyle: React.CSSProperties = {
  display: "block",
  maxWidth: "100%",
  maxHeight: "70vh",
  margin: "0 auto",
  objectFit: "contain",
};

const PreviewThumbnail = ({
  image,
  onClick,
}: {
  image: PreviewImage;
  onClick: (image: PreviewImage) => void;
}) => {
  const [hovered, setHovered] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setHovered(false);
    event.currentTarget.blur();
    onClick(image);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      title={`Visualiser ${image.name || image.id}`}
      aria-label={`Aperçu de ${image.name || image.id}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        width: PREVIEW_THUMB_SIZE,
        height: PREVIEW_THUMB_SIZE,
        flexShrink: 0,
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid rgba(72,72,72,.15)",
        backgroundColor: "#eee",
        padding: 0,
        cursor: "pointer",
      }}
    >
      <img
        src={image.url}
        alt={image.name || image.id}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          pointerEvents: "none",
        }}
      />
      {hovered && (
        <span
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.25)",
            pointerEvents: "none",
          }}
        >
          <AddIcon style={{ color: "#fff", fontSize: 22 }} />
        </span>
      )}
    </button>
  );
};

const scrollButtonStyle: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 1,
  backgroundColor: "rgba(255,255,255,0.92)",
  boxShadow: "0 1px 4px rgba(0,0,0,.15)",
  padding: 4,
};

const PreviewImageGrid = ({
  images,
  onImageClick,
}: {
  images: PreviewImage[];
  onImageClick: (image: PreviewImage) => void;
}) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: `repeat(${PREVIEW_COLUMNS}, ${PREVIEW_THUMB_SIZE}px)`,
      gridTemplateRows: `repeat(${MAX_PREVIEW_ROWS}, ${PREVIEW_THUMB_SIZE}px)`,
      gap: 4,
      justifyContent: "center",
    }}
  >
    {images.map((image) => (
      <PreviewThumbnail key={image.id} image={image} onClick={onImageClick} />
    ))}
  </div>
);

const PreviewImageRow = ({
  images,
  onImageClick,
}: {
  images: PreviewImage[];
  onImageClick: (image: PreviewImage) => void;
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = () => {
    const el = scrollRef.current;
    if (!el) return;

    const atStart = el.scrollLeft <= 0;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;

    setCanScrollLeft(!atStart);
    setCanScrollRight(!atEnd);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollButtons();

    el.addEventListener("scroll", updateScrollButtons);
    window.addEventListener("resize", updateScrollButtons);

    const observer = new ResizeObserver(updateScrollButtons);
    observer.observe(el);

    return () => {
      el.removeEventListener("scroll", updateScrollButtons);
      window.removeEventListener("resize", updateScrollButtons);
      observer.disconnect();
    };
  }, [images]);

  const scroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;

    scrollRef.current.scrollBy({
      left: direction === "left" ? -ROW_SCROLL_STEP : ROW_SCROLL_STEP,
      behavior: "smooth",
    });
  };

  return (
    <div style={{ position: "relative" }}>
      {canScrollLeft && (
        <IconButton
          aria-label="Images précédentes"
          onClick={() => scroll("left")}
          style={{ ...scrollButtonStyle, left: 4 }}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
      )}
      <div
        ref={scrollRef}
        style={{
          display: "flex",
          flexDirection: "row",
          gap: 4,
          overflowX: "auto",
          overflowY: "hidden",
          padding: "0 0 2px",
          scrollBehavior: "smooth",
        }}
      >
        {images.map((image) => (
          <PreviewThumbnail key={image.id} image={image} onClick={onImageClick} />
        ))}
      </div>
      {canScrollRight && (
        <IconButton
          aria-label="Images suivantes"
          onClick={() => scroll("right")}
          style={{ ...scrollButtonStyle, right: 4 }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      )}
    </div>
  );
};

const LabelSummaryImagePreview = ({
  images,
  total,
  layout = "row",
  dialogClasses,
}: Props) => {
  const sourceImages = useMemo(
    () => images.slice(0, MAX_PREVIEW_IMAGES),
    [images],
  );

  const sourceImagesKey = useMemo(
    () => buildSourceImagesKey(sourceImages),
    [sourceImages],
  );

  const [validPreviewImages, setValidPreviewImages] = useState<PreviewImage[]>(
    [],
  );
  const [checkedSourceKey, setCheckedSourceKey] = useState("");
  const [previewImage, setPreviewImage] = useState<PreviewImage | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  useEffect(() => {
    if (sourceImages.length === 0) {
      setValidPreviewImages([]);
      setCheckedSourceKey("");
      return;
    }

    let cancelled = false;

    filterReachableImages(sourceImages).then((reachableImages) => {
      if (!cancelled) {
        setValidPreviewImages(reachableImages);
        setCheckedSourceKey(sourceImagesKey);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [sourceImages, sourceImagesKey]);

  useEffect(() => {
    if (!previewImage) {
      setPreviewLoading(false);
      setPreviewError(false);
    }
  }, [previewImage]);

  const closePreview = () => setPreviewImage(null);

  const handleOpenPreview = (image: PreviewImage) => {
    setPreviewImage(image);
    setPreviewLoading(true);
    setPreviewError(false);
  };

  const isPreviewReady = checkedSourceKey === sourceImagesKey;

  if (!isPreviewReady || validPreviewImages.length === 0) {
    return null;
  }

  const previewTitle = previewImage?.name || previewImage?.id || "";

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div
          style={{
            fontWeight: 600,
            color: "#333",
          }}
        >
          Aperçu des images :
          {total > validPreviewImages.length && (
            <span style={{ fontWeight: 400, color: "#888" }}>
              {" "}
              (extrait : {validPreviewImages.length} sur {total})
            </span>
          )}
        </div>
        {layout === "row" ? (
          <PreviewImageRow
            images={validPreviewImages}
            onImageClick={handleOpenPreview}
          />
        ) : (
          <PreviewImageGrid
            images={validPreviewImages}
            onImageClick={handleOpenPreview}
          />
        )}
      </div>

      <Dialog
        open={!!previewImage}
        onClose={closePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          disableTypography
          className={dialogClasses?.filePreviewDialogTitle}
          style={
            dialogClasses?.filePreviewDialogTitle
              ? undefined
              : {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  paddingRight: 8,
                }
          }
        >
          <span
            className={dialogClasses?.filePreviewDialogTitleText}
            style={
              dialogClasses?.filePreviewDialogTitleText
                ? undefined
                : {
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: 20,
                    fontWeight: 500,
                  }
            }
          >
            {previewTitle}
          </span>
          <IconButton
            aria-label="Fermer"
            onClick={closePreview}
            className={dialogClasses?.filePreviewDialogCloseButton}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {previewLoading && !previewError && (
            <small>Chargement de l&apos;aperçu...</small>
          )}
          {previewError ? (
            <Typography color="error" style={{ fontSize: 14 }}>
              Impossible de charger l&apos;image.
            </Typography>
          ) : (
            previewImage && (
              <img
                src={previewImage.url}
                alt={previewTitle}
                className={dialogClasses?.filePreviewImage}
                style={
                  dialogClasses?.filePreviewImage
                    ? undefined
                    : defaultPreviewImageStyle
                }
                onLoad={() => setPreviewLoading(false)}
                onError={() => {
                  setPreviewLoading(false);
                  setPreviewError(true);
                }}
              />
            )
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LabelSummaryImagePreview;
